import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type DragEvent,
  type Key,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type RefObject,
} from 'react';
import {
  measureElement as measureRow,
  useVirtualizer,
  useWindowVirtualizer,
  type Virtualizer,
} from '@tanstack/react-virtual';
import { MESSAGE_SORT_LABELS, MESSAGE_SORT_SHORT_LABELS, type MessageSortOrder, type MessageSummary } from '@/api';
import { usePreferences } from '@/app/preferences';
import { Icon } from '@/components/Icon';
import { MessageListSkeleton } from '@/components/Skeleton';
import { FINE_POINTER_QUERY, useHandheld, useMediaQuery } from '@/hooks/useMediaQuery';
import { endMessageDrag, MESSAGE_DRAG_TYPE, startMessageDrag } from '@/features/dnd/messageDrag';
import { useTheme } from '@/hooks/useTheme';
import { avatarColor, avatarInitial } from '@/utils/avatarColor';
import { cssVars } from '@/utils/css';
import { displayName, formatListDate } from '@/utils/format';
import { useSwipeActions } from './useSwipeActions';
import type { AccountChip } from './unifiedRoute';
import { rowKey } from './rowKey';
import { isConversationRow, participantParts, participantsText } from './conversationRows';
import type { ConversationSummary, Label } from '@/api/types';
import { RowLabelChips } from '@/features/labels/LabelChips';

/**
 * Virtualized message list.
 *
 * A folder can hold tens of thousands of messages, so rows are windowed with
 * @tanstack/react-virtual: only the visible slice is in the DOM, which is what
 * keeps j/k scrolling at 60fps on a big Inbox.
 *
 * ## Keyboard/ARIA model (see docs/plan/06-frontend-v1.md §11)
 *
 * The list is a *composite widget*: the scroll container is the single tab stop
 * and the only thing that ever holds DOM focus, and the cursor row is exposed
 * with `aria-activedescendant`. A roving tabindex is the wrong pattern here,
 * because the element that would hold focus is virtualized: scroll the cursor
 * row out of the window and React unmounts it, which drops focus to `<body>`
 * and silently kills every subsequent keystroke. With activedescendant the
 * focused element is the container, which is never unmounted.
 *
 * The one hazard of activedescendant under virtualization is a *dangling*
 * reference — pointing at an id that is no longer in the DOM. That is handled
 * explicitly: if the cursor row falls outside the virtual window (the user
 * scrolled away with the mouse) it is force-rendered at its real offset, and if
 * its offset cannot be resolved the attribute is omitted entirely rather than
 * left dangling.
 *
 * Roles are `grid`/`row`/`gridcell`, not `listbox`/`option`, because a row owns
 * its own interactive controls (the star) and `option` has no valid way to
 * contain a focusable control. Rows carry `aria-rowindex` and the grid
 * `aria-rowcount`: with ~20 of 10 000 rows in the DOM, a screen reader would
 * otherwise announce "1 of 20" in a folder of thousands. (Not
 * `aria-setsize`/`aria-posinset`: ARIA allows those on treegrid rows only,
 * and axe flags them on a grid.)
 *
 * ## Two scrollers, one widget (ADR 0048 §5.3)
 *
 * On desktop the grid container is the scroller and the virtualizer is bound
 * to it. On handheld the *document* scrolls and the container grows to its
 * content, so an element-scroll virtualizer bound to it would see every row as
 * visible — and the load-more trigger below would fetch page after page until
 * the whole folder was in memory. There the list uses the window virtualizer
 * instead, with the grid's offset in the document as its `scrollMargin`. Both
 * hooks are always called (hooks cannot be conditional) and the unused one is
 * disabled. Everything above is unchanged by the switch: the container is
 * still the tab stop, still owns `aria-activedescendant` and every delegated
 * handler; it simply no longer scrolls.
 */

/**
 * Where a context menu was asked for, and by what (ADR 0019).
 *
 * `anchor` is a point for a right-click and the row's box for the keyboard and
 * long-press paths, which is what every desktop toolkit does and what screen
 * readers teach.
 */
export interface MessageContextMenuRequest {
  index: number;
  anchor: { x: number; y: number } | DOMRect;
  source: 'pointer' | 'keyboard' | 'touch';
}

export interface MessageListProps {
  messages: MessageSummary[];
  /** The open row's key (see `rowKey.ts`), not a bare message id. */
  selectedId: string | undefined;
  /** Index of the j/k keyboard cursor; scrolled into view automatically. */
  cursorIndex: number;
  isLoading: boolean;
  hasMore: boolean;
  onSelect: (message: MessageSummary, index: number) => void;
  /** Move the cursor without opening a message (arrow keys, Home/End, click). */
  onCursorChange: (index: number) => void;
  onToggleFlag: (message: MessageSummary) => void;
  onLoadMore: () => void;
  /**
   * Right-click, long-press, `Shift+F10` or the `ContextMenu` key on a row.
   * The list only reports *where*; `MailView` owns the menu and the verbs.
   */
  /**
   * The search this list is showing results for, when it is showing results
   * rather than a folder. It changes only the empty state: no results for a
   * query and an empty folder are different facts, and saying "this folder is
   * empty" to someone who just searched is a lie about their mailbox.
   */
  searchQuery?: string;
  /**
   * The order the list is in, the orders this account's server can actually
   * perform, and the handler that changes it. All three or none: the control
   * is not rendered without a handler, and it never offers an order the
   * server would refuse (`Paginated.sortsAvailable`).
   */
  sort?: MessageSortOrder;
  sortsAvailable?: MessageSortOrder[];
  onSortChange?: (sort: MessageSortOrder) => void;
  onContextMenu?: (request: MessageContextMenuRequest) => void;
  /** Double-click or `Shift+Enter`: open the message in its own window (ADR 0020). */
  onOpenInWindow?: (message: MessageSummary, index: number) => void;
  /**
   * The grid container, exposed so the menu can hand focus back to it. Focus
   * must never return to a row: virtualization can unmount one at any time.
   */
  gridRef?: RefObject<HTMLDivElement | null>;
  /**
   * The list is mounted but not on screen: the handheld message screen hides
   * the list pane rather than unmounting it. With the document as the
   * scroller, a live window virtualizer would read the *message's* scrolling
   * as the list's — fetching pages while someone reads, and scrolling the page
   * to the cursor row on every j/k — so it is switched off while hidden.
   */
  inactive?: boolean;
  /**
   * What a swipe does, for chunk F's `useSwipeActions` (ADR 0048 §6.1). The
   * list only carries it to the integration point below; `MailView` decides
   * the verbs and whether swiping is on at all (never in Trash or Junk).
   */
  swipe?: SwipeHandlers;
  /**
   * Multi-select (doc 06 §5). The ids `MailView` holds as selected, already
   * pruned to rows that are loaded. Non-empty means *selection mode*: on
   * handheld a tap then toggles a row instead of opening it.
   */
  selectedIds?: ReadonlySet<string>;  /* row keys, see `rowKey.ts` */
  /**
   * Toggle one row in or out of the selection: Ctrl/Cmd-click on either
   * layout; on handheld also a long-press, a tap on the avatar, and any tap
   * while in selection mode. Without it the list is single-select and a
   * long-press opens the context menu, exactly as before.
   */
  onToggleSelected?: (message: MessageSummary, index: number) => void;
  /** Shift-click: select from the selection's anchor to this row. */
  onSelectRange?: (index: number) => void;
  /**
   * Which mailbox each row is from, keyed by account id (ADR 0056 §8).
   *
   * Present only in the unified view; absent, a row renders exactly as it
   * always has. A row whose account is not in the map simply gets no chip
   * rather than a blank one.
   */
  accountChips?: ReadonlyMap<string, AccountChip>;
  /**
   * The folder a row is in, by name — present only while the list holds rows
   * from several folders (a cross-folder search). A row for which it answers
   * `undefined` simply gets no chip.
   */
  folderLabelOf?: (message: MessageSummary) => string | undefined;
  /**
   * The labels a row carries, resolved against ITS mailbox's labels
   * (plan/17 §5) — the caller owns the lists. Absent, no chips.
   */
  labelsOf?: (message: MessageSummary) => readonly Label[];
  /**
   * What the search looked through, for the no-results sentence. Absent means
   * the folder the list is showing, which is what a search has always meant.
   */
  searchScopeNote?: string;
  /**
   * What an empty list says instead of "This folder is empty…" — a label's
   * view is not a folder (plan/17).
   */
  emptyNote?: string;
  /**
   * What dragging this row carries onto the folder tree (#168): the selection
   * when the row is in it, else the row. `MailView` decides, because it owns
   * the selection; `undefined` (or no prop) means the row does not drag.
   */
  dragRows?: (message: MessageSummary) => { rows: MessageSummary[]; fromSelection: boolean } | undefined;
}

/** The archive/delete pair a swipe commits, and whether swiping is on. */
export interface SwipeHandlers {
  /**
   * Absent when there is nowhere to archive to — a mailbox with no Archive
   * folder — in which case the right-swipe has no action and the hook lets the
   * row spring back instead of committing one.
   */
  onArchive?: ((message: MessageSummary) => void) | undefined;
  onDelete: (message: MessageSummary) => void;
  enabled: boolean;
}

/** Swiping off: what the integration point passes when `MailView` gives none. */
export const SWIPE_OFF: SwipeHandlers = {
  onDelete: () => undefined,
  enabled: false,
};

/** Touch long-press, per ADR 0019: 500 ms with no more than 8 px of drift. */
const LONG_PRESS_MS = 500;
const LONG_PRESS_SLOP_PX = 8;

const ESTIMATED_ROW_HEIGHT = 84;
/*
 * Handheld rows are a fixed height (§6, `--shell-row-height-handheld`): 72 px
 * with a snippet line, 56 px without one. The estimate matching the real
 * height is what lets scroll restoration land on the right row before any row
 * has been measured.
 */
const HANDHELD_ROW_HEIGHT = 72;
const HANDHELD_ROW_HEIGHT_NO_SNIPPET = 56;
/* Compact density (mail-list.css): 56 px / the 44-px touch target on a phone,
   about 56 px measured on a desktop. */
const COMPACT_ROW_HEIGHT = 56;
const HANDHELD_COMPACT_ROW_HEIGHT = 56;
const HANDHELD_COMPACT_ROW_HEIGHT_NO_SNIPPET = 44;
const OVERSCAN = 8;
const PAGE_STEP = 10;

/**
 * The list cannot be scrolled to a row right now: it is inside a hidden pane,
 * or it has a height in the DOM that the virtualizer has not observed yet (it
 * still holds the 0 from while it was hidden).
 */
function listNotMeasurable(grid: HTMLElement | null, known: { height: number } | null): boolean {
  if (!grid) return false;
  if (inHiddenSubtree(grid)) return true;
  return known !== null && known.height === 0 && grid.getBoundingClientRect().height > 0;
}

/** Some ancestor is `display: none`: the element has no box to measure. */
function inHiddenSubtree(element: Element): boolean {
  for (let node: Element | null = element; node !== null; node = node.parentElement) {
    if (getComputedStyle(node).display === 'none') return true;
  }
  return false;
}

/**
 * Row measurement that ignores the zero a hidden list reports (#205).
 *
 * Where only one pane shows (the reading pane off, a narrow window), opening a
 * message hides the list with `display: none`, and the ResizeObserver then
 * reports every rendered row as 0 px tall. On the way back each row "grows"
 * from 0 to its real height, the virtualizer reads every one as an item above
 * the fold and shifts the scroll by its height, and the list landed some
 * thirteen rows down with the row that was open nowhere in view. A size taken
 * while hidden is not a size: keep the one already known (or the estimate).
 */
export function keepSizeWhileHidden<TScroll extends Element | Window>(
  element: Element,
  entry: ResizeObserverEntry | undefined,
  instance: Virtualizer<TScroll, Element>,
): number {
  const size = measureRow(element, entry, instance);
  if (size > 0 || !inHiddenSubtree(element)) return size;
  const index = instance.indexFromElement(element);
  return instance.itemSizeCache.get(instance.options.getItemKey(index)) ?? instance.options.estimateSize(index);
}

export function MessageList({
  messages,
  selectedId,
  cursorIndex,
  isLoading,
  hasMore,
  onSelect,
  onCursorChange,
  onToggleFlag,
  onLoadMore,
  searchQuery,
  sort,
  sortsAvailable,
  onSortChange,
  onContextMenu,
  onOpenInWindow,
  gridRef,
  inactive = false,
  swipe,
  selectedIds,
  onToggleSelected,
  onSelectRange,
  accountChips,
  folderLabelOf,
  labelsOf,
  searchScopeNote,
  emptyNote,
  dragRows,
}: MessageListProps) {
  const localRef = useRef<HTMLDivElement>(null);
  // One element, two owners: the virtualizer's scroll element and the thing
  // `MailView` hands focus back to when the menu closes. It is also the element
  // chunk F's swipe hook attaches to (see the integration point below).
  const scrollRef = gridRef ?? localRef;
  const idPrefix = useId();
  const rowId = useCallback((index: number) => `${idPrefix}row-${index}`, [idPrefix]);

  // Scrolling the cursor into view is motion the user did not initiate, so it
  // follows the same rule as the rest of the app's animation.
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const handheld = useHandheld();
  // Settings → General → "Show message snippets in the list".
  const showSnippets = usePreferences().preferences.showSnippets;
  const multiselectable = onToggleSelected !== undefined;
  const selecting = selectedIds !== undefined && selectedIds.size > 0;
  // On handheld the long-press is selection's, not the context menu's (the
  // owner: "long press for multi-select"); a touchscreen laptop at desktop
  // width keeps ADR 0019's long-press menu.
  const pressSelects = handheld && multiselectable;
  /*
   * Rows drag only under a mouse or trackpad (doc 06: no touch drag in v1). On
   * a touch screen the long-press is selection's, and iPadOS would otherwise
   * start a native drag from that same press.
   */
  const finePointer = useMediaQuery(FINE_POINTER_QUERY);
  const rowsDrag = dragRows !== undefined && finePointer && !handheld;

  const compact = useTheme().density === 'compact';
  const estimateSize = handheld
    ? compact
      ? showSnippets
        ? HANDHELD_COMPACT_ROW_HEIGHT
        : HANDHELD_COMPACT_ROW_HEIGHT_NO_SNIPPET
      : showSnippets
        ? HANDHELD_ROW_HEIGHT
        : HANDHELD_ROW_HEIGHT_NO_SNIPPET
    : compact
      ? COMPACT_ROW_HEIGHT
      : ESTIMATED_ROW_HEIGHT;

  /*
   * Where the grid starts in the document, for the window virtualizer: its
   * rows are laid out from 0 inside the grid, the window's scroll offset is
   * measured from the top of the page. Everything above the grid (app bar,
   * title, arrivals pill, chips, sort) can change height, so this is
   * re-measured after every render and on resize, and only a real change
   * costs a re-render. `scrollPaddingStart` is the sticky app bar, so a row
   * scrolled "into view" by j/k does not land underneath it.
   */
  const [windowGeometry, setWindowGeometry] = useState({ scrollMargin: 0, paddingStart: 0 });
  const measureWindowGeometry = useCallback(() => {
    const grid = scrollRef.current;
    if (!grid) return;
    const scrollMargin = Math.round(grid.getBoundingClientRect().top + window.scrollY);
    const appBar = document.querySelector('.topbar');
    const paddingStart =
      appBar && getComputedStyle(appBar).position === 'sticky'
        ? Math.round(appBar.getBoundingClientRect().height)
        : 0;
    setWindowGeometry((current) =>
      current.scrollMargin === scrollMargin && current.paddingStart === paddingStart
        ? current
        : { scrollMargin, paddingStart },
    );
  }, [scrollRef]);
  /*
   * Back from a message, the list's window virtualizer is switched on again —
   * and a virtualizer that starts watching a scroller first scrolls it to the
   * offset it last knew, which it read during this render, before the shell's
   * `ScrollRestoration` put the page back where the list was. Left alone it
   * undoes the restoration and every return lands at the top. So on that one
   * transition it is told where the page already is. This effect is declared
   * above the virtualizer hooks on purpose: effects run in declaration order,
   * and it has to land before the virtualizer's own layout effect.
   */
  const windowVirtualizerRef = useRef<ReturnType<typeof useWindowVirtualizer<Element>> | null>(null);
  const wasActive = useRef(false);
  useLayoutEffect(() => {
    const active = handheld && !inactive;
    if (active && !wasActive.current && windowVirtualizerRef.current) {
      windowVirtualizerRef.current.scrollOffset = window.scrollY;
    }
    wasActive.current = active;
    if (active) measureWindowGeometry();
  });
  useEffect(() => {
    if (!handheld || inactive) return;
    window.addEventListener('resize', measureWindowGeometry);
    return () => window.removeEventListener('resize', measureWindowGeometry);
  }, [handheld, inactive, measureWindowGeometry]);

  const shared = {
    count: messages.length,
    estimateSize: () => estimateSize,
    overscan: OVERSCAN,
    // The row's identity on screen, not the message id: the merged list can
 // hold the same id twice, once per mailbox (see `rowKey.ts`).
    getItemKey: (index: number) => {
      const message = messages[index];
      return message ? rowKey(message) : index;
    },
    measureElement: keepSizeWhileHidden,
  };
  const elementVirtualizer = useVirtualizer({
    ...shared,
    getScrollElement: () => scrollRef.current,
    enabled: !handheld,
  });
  const windowVirtualizer = useWindowVirtualizer({
    ...shared,
    scrollMargin: windowGeometry.scrollMargin,
    scrollPaddingStart: windowGeometry.paddingStart,
    enabled: handheld && !inactive,
  });
  windowVirtualizerRef.current = windowVirtualizer;
  // The two hooks return the same shape; only the scroll element differs.
  const virtualizer = handheld ? windowVirtualizer : elementVirtualizer;
  // Row offsets are document offsets in window mode; rows are positioned
  // inside the grid, so the grid's own offset comes back off.
  const rowOffset = handheld ? windowGeometry.scrollMargin : 0;

  // Keep the keyboard cursor on screen as j/k walk the list. Single steps are
  // instant (Gmail-fast triage); only a big jump (Home/End/PageUp) animates,
  // and never when the user asked for reduced motion.
  //
  // Only when the cursor *moves* (or first lands on a loaded row). A page
  // appended by load-more changes `messages.length` and nothing else, and
  // re-running this then dragged the list back up to the cursor row — row 0,
  // as often as not — the moment the next page arrived under the reader's
  // scroll. `previousCursor` is the last cursor actually scrolled to.
  const previousCursor = useRef<number | undefined>(undefined);
  useEffect(() => {
    // A hidden list must not scroll the page: on handheld the page is the
    // message being read.
    if (inactive) return;
    if (cursorIndex < 0 || cursorIndex >= messages.length) return;
    // Hidden by a stacked layout (#205), or shown again a moment ago and the
    // virtualizer has not seen its new height yet: a scroll worked out against
    // a 0-px viewport puts the row above the top edge. The cursor is left
    // "unscrolled to"; `revealCursor` below does it once the list is back.
    if (!handheld && listNotMeasurable(scrollRef.current, virtualizer.scrollRect)) return;
    const previous = previousCursor.current;
    previousCursor.current = cursorIndex;
    if (previous === cursorIndex) return;
    // On handheld the first landing belongs to the page: a new list starts at
    // the top and a returning one where ScrollRestoration put it. Scrolling to
    // the cursor here would tuck the folder title and chips under the app bar.
    if (previous === undefined && handheld) return;
    const jumped = previous !== undefined && Math.abs(cursorIndex - previous) > 1;
    virtualizer.scrollToIndex(cursorIndex, {
      align: 'auto',
      behavior: jumped && !prefersReducedMotion ? 'smooth' : 'auto',
    });
  }, [cursorIndex, handheld, inactive, messages.length, prefersReducedMotion, scrollRef, virtualizer]);

  /*
   * The list shown again after a stacked layout hid it (#205): the reading
   * pane "off", or a window narrow enough for one pane. `display: none` threw
   * its scroll position away, so on the way back the row that was open is put
   * in view, after the virtualizer has measured the list again (the frame
   * after the resize). Desktop only: on handheld the page scrolls, and
   * ScrollRestoration puts it back.
   */
  const revealCursor = useRef<() => void>(() => undefined);
  revealCursor.current = () => {
    if (cursorIndex < 0 || cursorIndex >= messages.length) return;
    previousCursor.current = cursorIndex;
    virtualizer.scrollToIndex(cursorIndex, { align: 'auto' });
  };
  // The grid is only drawn once there are rows (skeleton and empty states
  // come first), so the observer is (re)attached when they arrive.
  const hasRows = messages.length > 0;
  useEffect(() => {
    const grid = scrollRef.current;
    if (handheld || !hasRows || !grid || typeof ResizeObserver === 'undefined') return undefined;
    let hidden = false;
    let frame = 0;
    const observer = new ResizeObserver(([entry]) => {
      const nowHidden = (entry?.contentRect.height ?? 0) === 0;
      if (hidden && !nowHidden) {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => revealCursor.current());
      }
      hidden = nowHidden;
    });
    observer.observe(grid);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [handheld, hasRows, scrollRef]);

  /*
   * ── Chunk F integration point (ADR 0048 §6.1) ──────────────────────────
   * Swipe right = archive, left = delete. The hook attaches its own pointer
   * listeners to the grid container and renders nothing; rows stay
   * handler-free, as the composite-widget contract above requires. Applied at
   * merge as one call here, with `swipe` added to the destructured props:
   *
   *   useSwipeActions(scrollRef, swipe ?? SWIPE_OFF);
   *
   * `swipe` comes from `MailView` (the same archive/remove the keymap calls,
   * and `enabled` false outside handheld, in Trash and in Junk).
   */
  // The hook reports the swiped row's INDEX (it reads the row's current
  // `data-index` at release, so a row that moved under the finger still names
  // the right message); `MailView`'s verbs take the message itself.
  const swipeActions = swipe ?? SWIPE_OFF;
  useSwipeActions(scrollRef, {
    enabled: swipeActions.enabled,
    ...(swipeActions.onArchive
      ? {
          onArchive: (index: number) => {
            const message = messages[index];
            if (message) swipeActions.onArchive?.(message);
          },
        }
      : {}),
    onDelete: (index) => {
      const message = messages[index];
      if (message) swipeActions.onDelete(message);
    },
  });

  // Infinite scroll: pull the next page as the last rendered row approaches.
  const items = virtualizer.getVirtualItems();
  const lastItem = items[items.length - 1];
  useEffect(() => {
    if (!hasMore || isLoading || !lastItem) return;
    if (lastItem.index >= messages.length - 5) onLoadMore();
  }, [hasMore, isLoading, lastItem, messages.length, onLoadMore]);

  const moveCursorTo = useCallback(
    (index: number) => {
      if (messages.length === 0) return;
      const clamped = Math.min(Math.max(index, 0), messages.length - 1);
      onCursorChange(clamped);
    },
    [messages.length, onCursorChange],
  );

  /**
   * ## Delegated row triggers (ADR 0019 §2.3, ADR 0020 §3.5)
   *
   * `contextmenu`, long-press, `dblclick` and the keyboard menu keys are all
   * handled **on the container**, resolved to a row with
   * `closest('[data-index]')`. Rows therefore gain no handlers and no
   * `tabIndex`, and the activedescendant contract above stays exactly as it
   * was: opening a menu moves the *cursor* (`onCursorChange`) and never calls
   * `onSelect`, so a right-click cannot open a message the way a click does.
   */
  const indexFromEvent = useCallback(
    (target: EventTarget | null): number | undefined => {
      if (!(target instanceof Element)) return undefined;
      const row = target.closest('[data-index]');
      if (!row) return undefined;
      const index = Number(row.getAttribute('data-index'));
      if (!Number.isInteger(index) || index < 0 || index >= messages.length) return undefined;
      return index;
    },
    [messages.length],
  );

  const rowRect = useCallback(
    (index: number) => document.getElementById(rowId(index))?.getBoundingClientRect(),
    [rowId],
  );

  const requestMenu = useCallback(
    (index: number, anchor: { x: number; y: number } | DOMRect, source: MessageContextMenuRequest['source']) => {
      if (!onContextMenu) return;
      onCursorChange(index);
      onContextMenu({ index, anchor, source });
    },
    [onContextMenu, onCursorChange],
  );

  const handleContextMenu = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const index = indexFromEvent(event.target);
      // Off a row — the scroll gutter, say — the native menu is the right
      // answer and is left alone. It is suppressed on rows only.
      if (index === undefined) return;
      // Chrome on Android fires `contextmenu` for a long touch. On handheld
      // that touch is the selection's (the timer below), so the event only
      // has to be kept from opening the native menu on top of it.
      if (pressSelects && lastPointerType.current === 'touch') {
        event.preventDefault();
        return;
      }
      if (!onContextMenu) return;
      event.preventDefault();
      scrollRef.current?.focus({ preventScroll: true });
      requestMenu(index, { x: event.clientX, y: event.clientY }, 'pointer');
    },
    [indexFromEvent, onContextMenu, pressSelects, requestMenu, scrollRef],
  );

  /**
   * Double-click opens the window *in addition* to what the two clicks already
   * did (ADR 0020 §3.5). There is no click debounce: delaying every single
   * click by 300 ms to tell the two apart would make opening a message in the
   * pane feel slow, for a gesture almost nobody makes by accident. The second
   * `click` is idempotent — it navigates to the URL that is already open and
   * finds the row already `\Seen` from the first click's optimistic patch.
   */
  const handleDoubleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const index = indexFromEvent(event.target);
      if (index === undefined || !onOpenInWindow) return;
      const message = messages[index];
      if (!message) return;
      onOpenInWindow(message, index);
    },
    [indexFromEvent, messages, onOpenInWindow],
  );

  /*
   * Long-press is the touch equivalent of a right-click on desktop widths, and
   * the way into selection mode on handheld. Either way, once it has fired,
   * the click the same touch produces on release is swallowed — otherwise it
   * would open the row that was just selected, or select it straight back out.
   */
  const longPress = useRef<{
    timer: ReturnType<typeof setTimeout>;
    x: number;
    y: number;
  } | null>(null);
  const swallowClick = useRef(false);
  /** What the last press was made with; Android sends `contextmenu` for a long touch. */
  const lastPointerType = useRef<string>('mouse');

  const cancelLongPress = useCallback(() => {
    if (longPress.current) clearTimeout(longPress.current.timer);
    longPress.current = null;
  }, []);

  useEffect(() => cancelLongPress, [cancelLongPress]);

  /**
   * A row drag (#168), delegated to the container like every other row
   * gesture. The payload goes to the drag store, not the row: a virtualised
   * row can unmount while the pointer is over the sidebar. The data type is
   * our own and nothing else (no `text/plain`), so a row dropped on the
   * composer's editor inserts nothing; Firefox will not start a drag that
   * carries no data at all.
   */
  const handleDragStart = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      const index = indexFromEvent(event.target);
      const message = index === undefined ? undefined : messages[index];
      const drag = message && dragRows ? dragRows(message) : undefined;
      if (!drag || drag.rows.length === 0) {
        event.preventDefault();
        return;
      }
      cancelLongPress();
      const transfer = event.dataTransfer;
      // copyMove: Ctrl or ⌥ held on the drop copies instead (ADR 0108).
      transfer.effectAllowed = 'copyMove';
      transfer.setData(MESSAGE_DRAG_TYPE, JSON.stringify(drag.rows.map((row) => rowKey(row))));
      const image = dragImage(drag.rows);
      if (image && typeof transfer.setDragImage === 'function') {
        transfer.setDragImage(image, 16, 16);
        // The browser snapshots the element during this handler; it can go.
        requestAnimationFrame(() => image.remove());
      } else {
        image?.remove();
      }
      startMessageDrag(drag);
    },
    [cancelLongPress, dragRows, indexFromEvent, messages],
  );

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      lastPointerType.current = event.pointerType;
      swallowClick.current = false;
      if (event.pointerType !== 'touch' || (!onContextMenu && !pressSelects)) return;
      const index = indexFromEvent(event.target);
      if (index === undefined) return;
      const { clientX, clientY } = event;
      cancelLongPress();
      longPress.current = {
        x: clientX,
        y: clientY,
        timer: setTimeout(() => {
          longPress.current = null;
          swallowClick.current = true;
          const message = messages[index];
          // In selection mode too: a long-press there toggles like a tap, and
          // never re-opens a menu over the selection.
          if (pressSelects && onToggleSelected && message) {
            onCursorChange(index);
            onToggleSelected(message, index);
            return;
          }
          requestMenu(index, rowRect(index) ?? { x: clientX, y: clientY }, 'touch');
        }, LONG_PRESS_MS),
      };
    },
    [
      cancelLongPress,
      indexFromEvent,
      messages,
      onContextMenu,
      onCursorChange,
      onToggleSelected,
      pressSelects,
      requestMenu,
      rowRect,
    ],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const press = longPress.current;
      if (!press) return;
      if (
        Math.abs(event.clientX - press.x) > LONG_PRESS_SLOP_PX ||
        Math.abs(event.clientY - press.y) > LONG_PRESS_SLOP_PX
      ) {
        cancelLongPress();
      }
    },
    [cancelLongPress],
  );

  /**
   * Keys the list owns while it has focus. `j`/`k` and the action keys stay in
   * the global keymap (`MailView`) so they work from the reading pane too;
   * these are the composite-widget keys a grid is required to implement.
   *
   * Tab is deliberately absent: it is never intercepted, so focus leaves the
   * list for the reading pane exactly as the tab order says. Nothing here is a
   * focus trap.
   */
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const cursorMessage = messages[cursorIndex];

      /*
       * The keyboard path to the context menu is not optional (ADR 0019): a
       * right-click-only affordance is unusable without a mouse, and these two
       * keys are what every desktop toolkit and every screen reader teaches.
       * The menu is anchored to the cursor row's box.
       */
      if ((event.key === 'F10' && event.shiftKey) || event.key === 'ContextMenu') {
        if (!onContextMenu || !cursorMessage) return;
        requestMenu(cursorIndex, rowRect(cursorIndex) ?? { x: 0, y: 0 }, 'keyboard');
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          moveCursorTo(cursorIndex + 1);
          break;
        case 'ArrowUp':
          moveCursorTo(cursorIndex - 1);
          break;
        case 'PageDown':
          moveCursorTo(cursorIndex + PAGE_STEP);
          break;
        case 'PageUp':
          moveCursorTo(cursorIndex - PAGE_STEP);
          break;
        case 'Home':
          moveCursorTo(0);
          break;
        case 'End':
          moveCursorTo(messages.length - 1);
          break;
        case 'Enter':
        case ' ':
          if (!cursorMessage) return;
          // Shift+Enter is the keyboard equivalent of the double-click: the
          // window, not the pane (ADR 0020 §3.5).
          if (event.key === 'Enter' && event.shiftKey) {
            if (!onOpenInWindow) return;
            onOpenInWindow(cursorMessage, cursorIndex);
            break;
          }
          onSelect(cursorMessage, cursorIndex);
          break;
        default:
          return;
      }

      // Handled here; don't let the window-level keymap act on it twice.
      event.preventDefault();
      event.stopPropagation();
    },
    [
      cursorIndex,
      messages,
      moveCursorTo,
      onContextMenu,
      onOpenInWindow,
      onSelect,
      requestMenu,
      rowRect,
    ],
  );

  // Above every branch below, including the skeleton and the empty states:
  // the order is a property of the list, not of the rows that happen to be in
  // it, and a control that disappears while a page loads flickers on every
  // folder change.
  const sortBar =
    onSortChange !== undefined && sort !== undefined ? (
      <MessageSortSelect value={sort} available={sortsAvailable} onChange={onSortChange} />
    ) : null;

  if (isLoading && messages.length === 0) {
    // Skeleton rows, never a bare spinner (spec: "UX & Feel").
    return (
      <>
        {sortBar}
        <MessageListSkeleton rows={14} />
      </>
    );
  }

  const searching = searchQuery !== undefined && searchQuery.trim() !== '';

  if (messages.length === 0 && searching) {
    return (
      <>
        {sortBar}
        <div className="empty-state">
        <Icon name="search" size={32} />
        <p className="empty-state__title">No messages match “{searchQuery}”</p>
        {/*
          The scope is named because the search is scoped: it ran against this
          folder and not the whole mailbox, and a result of nothing means
          nothing HERE.
        */}
        <p className="muted">
          {searchScopeNote ?? 'This searches the folder you are in'}, by sender, recipient, subject
          and message text. Clear the search box to go back to the folder.
        </p>
        <p className="muted">
          You can also narrow it: <code>from:ana</code>, <code>subject:&quot;end of year&quot;</code>,{' '}
          <code>is:unread</code>, <code>label:&quot;Work&quot;</code>, <code>date:09-12-25</code> (month-day-year),{' '}
          <code>date:09-12-25..09-20-25</code>, <code>after:</code>/<code>before:</code>.
        </p>
        </div>
      </>
    );
  }

  if (messages.length === 0) {
    return (
      <>
        {sortBar}
        <div className="empty-state">
        <Icon name="inbox" size={32} />
        <p className="empty-state__title">Nothing here</p>
        {/*
          New mail does not arrive by itself in this beta — there is no SSE and
          no IDLE — so the empty state names the only thing that changes it.
        */}
        <p className="muted">
          {emptyNote ??
            'This folder is empty, or nothing matches the current filter. Refresh to check for new mail.'}
        </p>
        </div>
      </>
    );
  }

  const cursorInWindow = items.some((item) => item.index === cursorIndex);
  const cursorIsValid = cursorIndex >= 0 && cursorIndex < messages.length;
  // Force-render the cursor row when the user has scrolled it out of the
  // virtual window, so `aria-activedescendant` never dangles. In window mode
  // the row's own start is used: the scroll offset `getOffsetForIndex` gives
  // is clamped to the page's scroll range and less the sticky app bar, which
  // is where the page would scroll to, not where the row is.
  const strandedCursorOffset =
    cursorIsValid && !cursorInWindow
      ? handheld
        ? virtualizer.measurementsCache[cursorIndex]?.start
        : virtualizer.getOffsetForIndex(cursorIndex, 'start')?.[0]
      : undefined;
  const cursorIsRendered = cursorIsValid && (cursorInWindow || strandedCursorOffset !== undefined);

  const renderRow = (index: number, start: number, key: Key) => {
    const message = messages[index];
    if (!message) return null;
    const unread = !message.flags.includes('seen');
    const flagged = message.flags.includes('flagged');
    const identity = rowKey(message);
    const selected = identity === selectedId;
    const checked = selectedIds?.has(identity) ?? false;
    /*
     * The star is the same control in both layouts; what moves is its cell.
     * Desktop keeps it in the left meta column. On handheld (§6) that column
     * is the sender's avatar and the star sits at the right end of line 1 as
     * its own cell, with a 44-px hit area around a 20-px glyph.
     */
    const starCell = (
      <div className="message-row__meta" role="gridcell" aria-colindex={handheld ? 2 : 1}>
        <button
          type="button"
          className="message-row__star"
          data-flagged={flagged}
          aria-label={flagged ? 'Remove star' : 'Add star'}
          aria-pressed={flagged}
          // Not a tab stop: the list is one tab stop, and a row-owned control
          // would vanish from the tab order the moment it is virtualized away.
          // The keyboard path is `s` on the cursor row.
          tabIndex={-1}
          onClick={(event) => {
            event.stopPropagation();
            onToggleFlag(message);
          }}
        >
          <Icon name={flagged ? 'star-filled' : 'star'} size={handheld ? 20 : 16} />
        </button>
      </div>
    );
    const attachment = message.hasAttachments && <Icon name="attachment" size={14} />;
    const accountChip = accountChips?.get(message.accountId);
    const folderLabel = folderLabelOf?.(message);
    const rowLabels = labelsOf?.(message) ?? [];
    // A conversation row (ADR 0077) says who is in it and how many messages,
    // Gmail-style, and which other folders it spans. A message row is
    // rendered exactly as it always was.
    const conversation = isConversationRow(message) ? message.conversation : undefined;
    return (
      // Rows are deliberately not focusable and carry no key handlers: this is an
      // aria-activedescendant composite widget, so the scroll container owns both
      // DOM focus and every keystroke (see the grid's onKeyDown). Giving rows their
      // own tabIndex would create tab stops that vanish when virtualization unmounts
      // them. jsx-a11y cannot see the container-level handling, so these two rules
      // are false positives here.
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
      <div
        key={key}
        id={rowId(index)}
        role="row"
        // `aria-selected` is the *selection* while there is one (doc 06 §11:
        // selection, not the cursor); with none it is the open message, as
        // it has always been.
        aria-selected={selecting ? checked : selected}
        aria-rowindex={index + 1}
        className="message-row"
        data-selected={selected}
        {...(multiselectable ? { 'data-checked': checked } : {})}
        data-cursor={index === cursorIndex}
        data-unread={unread}
        {...(handheld ? { 'data-snippet': showSnippets } : {})}
        ref={virtualizer.measureElement}
        data-index={index}
        {...(rowsDrag ? { draggable: true } : {})}
        style={{ transform: `translateY(${start - rowOffset}px)` }}
        onClick={(event) => {
          // The release of a long-press that has already done its job.
          if (swallowClick.current) {
            swallowClick.current = false;
            return;
          }
          // Click and keyboard converge: the click moves the cursor as well as
          // opening the message, and focus lands on the list container so the
          // next j/k continues from the row that was clicked.
          scrollRef.current?.focus({ preventScroll: true });
          onCursorChange(index);
          if (onToggleSelected) {
            /*
             * Selection gestures (doc 06 §5). Shift-click extends from the
             * anchor; Ctrl/Cmd-click toggles one. On handheld, once anything is
             * selected a tap toggles rather than opens (Gmail), and the avatar
             * is a tap target of its own that toggles from a standing start —
             * a way in that is not a timed gesture.
             */
            if (event.shiftKey && onSelectRange) {
              onSelectRange(index);
              return;
            }
            const tappedAvatar =
              handheld &&
              event.target instanceof Element &&
              event.target.closest('.message-row__avatar') !== null;
            if (event.ctrlKey || event.metaKey || (handheld && (selecting || tappedAvatar))) {
              onToggleSelected(message, index);
              return;
            }
          }
          onSelect(message, index);
        }}
      >
        {/* Decoration: the sender is the first thing the row says anyway. */}
        {/* A selected row's avatar turns into the check (Gmail): the mark
            sits where the eye already goes for "who", costs no column on a
            390-px row, and is the avatar's tap target. Still decoration to a
            screen reader, which hears the row's `aria-selected`. */}
        {handheld && (
          <span
            className="avatar message-row__avatar"
            aria-hidden="true"
            data-checked={checked}
            style={cssVars({ '--avatar-color': avatarColor(message.from.email.toLowerCase()) })}
          >
            {checked ? <Icon name="check" size={20} /> : avatarInitial(message.from.name, message.from.email)}
          </span>
        )}
        {!handheld && starCell}

        <div className="message-row__body" role="gridcell" aria-colindex={handheld ? 1 : 2}>
          <div className="message-row__line">
            {/*
              Which mailbox this row is from (ADR 0056 §8). Rendered only when
              the list is showing more than one account, so a folder list is
              exactly what it was. The chip is TEXT tinted with the account's
              swatch — colour is the shortcut, never the signal — and its full
              address is read out before the sender.
            */}
            {accountChip && (
              <span
                className="message-row__account truncate"
                style={cssVars({ '--account-color': accountChip.color })}
              >
                <span className="visually-hidden">{`From mailbox ${accountChip.title}. `}</span>
                <span aria-hidden="true">{accountChip.label}</span>
              </span>
            )}
            {conversation ? (
              <ConversationParticipants conversation={conversation} />
            ) : (
              <span className="message-row__from truncate" dir="auto">
                {displayName(message.from)}
              </span>
            )}
            {!conversation && message.threadCount !== undefined && message.threadCount > 1 && (
              <span className="message-row__thread-count">{message.threadCount}</span>
            )}
            {!handheld && attachment}
            <span className="message-row__date">{formatListDate(message.date)}</span>
            {handheld && attachment}
          </div>
          <div className="message-row__subject truncate">
            {/* Which folder a cross-folder result is in: read before the
                subject, and printed, so it never depends on colour. */}
            {folderLabel !== undefined && (
              <span className="message-row__folder" title={`In ${folderLabel}`}>
                <span className="visually-hidden">In folder </span>
                {folderLabel}
                <span className="visually-hidden">. </span>
              </span>
            )}
            {conversation?.labels.map((label) => (
              <span key={label.folderId} className="message-row__label" data-role={label.role}>
                <span className="visually-hidden">Also in </span>
                {label.name}
                <span className="visually-hidden">. </span>
              </span>
            ))}
            {/* A snooze that woke (ADR 0076 §14), while it is still unread:
                the reason it is back at the top. Words, not only a tint. */}
            {unread && message.flags.includes('snooze_returned') && (
              <span className="message-row__snooze-chip">Returned from snooze</span>
            )}
            {/* The row's labels (plan/17): at most three and a "+N", so a
                phone row still has room for its subject. */}
            <RowLabelChips labels={rowLabels} />
            {/* Isolated, so a right-to-left subject neither reorders the
                chips before it nor takes their direction (#41). */}
            <bdi>{message.subject || '(no subject)'}</bdi>
          </div>
          {showSnippets && <div className="message-row__snippet truncate">{message.snippet}</div>}
          {/* State last, so a row reads as a sentence: who, what, when, then state. */}
          <span className="visually-hidden">
            {[
              conversation && conversation.messageCount > 1 ? `${conversation.messageCount} messages` : null,
              unread ? 'Unread' : null,
              flagged ? 'Starred' : null,
              message.hasAttachments ? 'Has attachment' : null,
            ]
              .filter(Boolean)
              .join(', ')}
          </span>
        </div>

        {handheld && starCell}
      </div>
    );
  };

  return (
    <>
      {sortBar}
      <div
        className="pane__scroll message-grid"
        ref={scrollRef}
        role="grid"
        tabIndex={0}
        aria-label="Messages"
        aria-rowcount={messages.length}
        aria-colcount={2}
        {...(multiselectable ? { 'aria-multiselectable': true } : {})}
        {...(cursorIsRendered ? { 'aria-activedescendant': rowId(cursorIndex) } : {})}
        onKeyDown={onKeyDown}
        onContextMenu={handleContextMenu}
        onDoubleClick={handleDoubleClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={cancelLongPress}
        onPointerCancel={cancelLongPress}
        onPointerLeave={cancelLongPress}
        {...(rowsDrag ? { onDragStart: handleDragStart, onDragEnd: endMessageDrag } : {})}
      >
        <div
          className="message-list"
          role="rowgroup"
          style={{ height: `${virtualizer.getTotalSize()}px` }}
        >
          {items.map((item) => renderRow(item.index, item.start, item.key))}
          {strandedCursorOffset !== undefined &&
            renderRow(cursorIndex, strandedCursorOffset, `cursor-${cursorIndex}`)}
        </div>
      </div>

      {hasMore && (
        <div className="list-footer">
          <button type="button" className="btn btn--ghost" onClick={onLoadMore} disabled={isLoading}>
            {isLoading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </>
  );
}

/**
 * A conversation's senders: "me, Ana, Bob (3)" — the ones with unread mail in
 * bold, the middle of a long line collapsed to "..", the message count after.
 * The full list is the title, for the names the line had to leave out.
 *
 * The names truncate and the count does not: it sits beside them, not inside
 * the ellipsis, so "Discord (120)" in a narrow column ends "Disc… (120)"
 * rather than losing the count (mail-list.css).
 */
function ConversationParticipants({ conversation }: { conversation: ConversationSummary }) {
  const parts = participantParts(conversation.participants);
  return (
    <span
      className="message-row__from message-row__from--conversation"
      title={participantsText(conversation.participants)}
    >
      <span className="message-row__participants truncate">
        {parts.map((part, index) => {
          if (part.kind === 'gap') {
            return (
              <span key={index} className="message-row__participant-gap">
                {' .. '}
              </span>
            );
          }
          const separator = index > 0 && parts[index - 1]?.kind === 'name' ? ', ' : '';
          return (
            <Fragment key={index}>
              {separator}
              <span className="message-row__participant" data-unread={part.unread} title={part.title} dir="auto">
                {part.label}
              </span>
            </Fragment>
          );
        })}
      </span>
      {conversation.messageCount > 1 && (
        <span className="message-row__count">{` (${conversation.messageCount})`}</span>
      )}
    </span>
  );
}

/**
 * The sort control.
 *
 * One native `<select>` of complete orders rather than a field picker beside a
 * direction toggle. Two controls could be put into a combination that does not
 * exist — this server may be able to sort by sender and not by size — and the
 * list of complete orders is exactly what the server advertises. A native
 * select is also already a correct keyboard and screen-reader control: it is
 * in the tab order, it has a visible `<label>`, and it announces its value.
 *
 * `available` is undefined only until the first page comes back. Rendering the
 * remembered order alone until then keeps the control from changing width and
 * from briefly offering orders this account may not have.
 */
function MessageSortSelect({
  value,
  available,
  onChange,
}: {
  value: MessageSortOrder;
  available: MessageSortOrder[] | undefined;
  onChange: (next: MessageSortOrder) => void;
}) {
  const id = useId();
  const labels = useHandheld() ? MESSAGE_SORT_SHORT_LABELS : MESSAGE_SORT_LABELS;
  const offered = available ?? [value];
  // A select whose value matches no option renders blank, so the current order
  // is always one of them even if the server has stopped offering it.
  const options = offered.includes(value) ? offered : [value, ...offered];
  // Only arrival order needs no IMAP SORT extension, so a server that offers
  // nothing else cannot sort at all, and saying so is better than a menu that
  // looks arbitrarily short.
  // Not before the server has said: the remembered order alone (an arrival
  // one, by default) is not "can only sort by arrival", and a note drawn and
  // withdrawn while the first page loads shoved the list 40 px down and back
  // (the phone's 0.25 layout shift on /mail).
  const arrivalOnly = available !== undefined && options.every((order) => order.startsWith('arrival-'));

  return (
    <div className="message-sort">
      <label className="message-sort__label" htmlFor={`${id}sort`}>
        Sort by
      </label>
      <select
        id={`${id}sort`}
        className="message-sort__select"
        value={value}
        {...(arrivalOnly ? { 'aria-describedby': `${id}note` } : {})}
        onChange={(event) => onChange(event.target.value as MessageSortOrder)}
      >
        {options.map((order) => (
          <option key={order} value={order}>
            {labels[order]}
          </option>
        ))}
      </select>
      {arrivalOnly && (
        <p className="message-sort__note muted" id={`${id}note`}>
          This mail server can only order messages by when they arrived.
        </p>
      )}
    </div>
  );
}

/**
 * The drag image (doc 06): a small card, "3 messages" over the first subject,
 * rather than the browser's ghost of one translucent row. Parked off screen,
 * because `setDragImage` needs an element in the document to snapshot.
 */
function dragImage(rows: readonly MessageSummary[]): HTMLElement | undefined {
  if (typeof document === 'undefined') return undefined;
  const card = document.createElement('div');
  card.className = 'message-drag-image';
  const count = document.createElement('strong');
  count.textContent = rows.length === 1 ? '1 message' : `${rows.length} messages`;
  const subject = document.createElement('span');
  subject.textContent = rows[0]?.subject || '(no subject)';
  card.append(count, subject);
  document.body.append(card);
  return card;
}
